import { useCallback, useEffect, useState } from 'react';
import { Order } from 'shared/types/cart/Order';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
const useOrderInfoData = (order?: Order) => {
  const { formatMessage } = useFormat({ name: 'thank-you' });
  const [orderNumber, setOrderNumber] = useState('');
  const [orderDate, setOrderDate] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('');
  const [deliveryMode, setDeliveryMode] = useState('');
  const [deliveryMethodKey, setDeliveryMethodKey] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [billingName, setBillingName] = useState('');
  const [paymentInfo, setPaymentInfo] = useState('');
  const [deliveryInstruction, setDeliveryInstruction] = useState('');
  const [paymentState, setPaymentState] = useState('');

  const { locale } = useLocale();

  const updateOrderNumber = useCallback(() => {
    const label = order?.orderNumber ?? '';
    setOrderNumber(label);
  }, [order?.orderNumber]);

  const updateOrderDate = useCallback(() => {
    let createdAt = new Date();
    if (order && order.createdAt !== undefined) {
      createdAt = new Date(order.createdAt);
    }
    const formattedDate = createdAt.toLocaleDateString(locale.replace('_', '-'), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const orderDate = order && order.createdAt !== undefined ? formattedDate : '';
    setOrderDate(orderDate);
  }, [order?.createdAt, locale]);

  const updateDeliveryMode = useCallback(() => {
    const deliveryMode = order?.custom?.deliveryOption ?? '';
    let deliveryModeLabel = formatMessage({ id: 'home.delivery', defaultMessage: 'Home Delivery' });
    if (deliveryMode === 'StoreCollection') {
      deliveryModeLabel = formatMessage({ id: 'collec.at.store', defaultMessage: 'Store collection' });
    }
    setDeliveryMode(deliveryModeLabel);
  }, [order?.custom?.deliveryOption, formatMessage]);

  const updateDeliveryMethod = useCallback(() => {
    const label = order?.shippingInfo?.description ?? '';
    setDeliveryMethod(label);
  }, [order?.shippingInfo?.description]);

  const updateDeliveryMethodKey = useCallback(() => {
    const key = order?.shippingInfo?.key ?? '';
    setDeliveryMethodKey(key);
  }, [order?.shippingInfo?.key]);

  const updateDeliveryDate = useCallback(() => {
    let date = new Date();
    if (order && order?.custom?.deliveryDate !== undefined) {
      date = new Date(order?.custom?.deliveryDate);
    }
    const formattedDate = date.toLocaleDateString(locale.replace('_', '-'), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const deliveryDate = order && order?.custom?.deliveryDate !== undefined ? formattedDate : '';
    setDeliveryDate(deliveryDate);
  }, [order?.custom?.deliveryDate]);

  const updateShippingAddress = useCallback(() => {
    if (order?.shippingAddress) {
      const { streetName, city, postalCode } = order.shippingAddress;
      const label = `${streetName}, ${city}, ${postalCode}`;
      setShippingAddress(label);
    }
  }, [order?.shippingAddress]);

  const updateBillingAddress = useCallback(() => {
    if (order?.billingAddress) {
      const { firstName = '', lastName = '', streetName = '', city = '', postalCode = '' } = order.billingAddress;
      const label = `${streetName}, ${city}, ${postalCode}`;
      setBillingAddress(label);
      setBillingName(firstName + ' ' + lastName);
    }
  }, [order?.billingAddress]);

  const updatePaymentInfo = useCallback(() => {
    const paymentMethodMapping: Record<string, string> = {
      mc: 'MASTER CARD',
    };

    let uniquePaymentMethods: string[] = [];
    if (order?.payments) {
      uniquePaymentMethods = [
        ...new Set(
          (order?.payments).map(
            (payment) => paymentMethodMapping[payment.paymentMethod] || payment.paymentMethod.toUpperCase(),
          ),
        ),
      ];
    }
    const formattedPaymentMethods = uniquePaymentMethods.join(', ');
    const payment = order?.payments?.[0];
    // const lastDigits = payment?.cardSummary;
    //const lastDigits = undefined;
    const label = formattedPaymentMethods;
    setPaymentInfo(label);
  }, [order?.payments]);

  const updateDeliveryInstruction = useCallback(() => {
    if (order?.custom?.deliveryInstruction) {
      const deliveryInstruction = order?.custom?.deliveryInstruction ?? '';
      setDeliveryInstruction(deliveryInstruction);
    }
  }, [order?.custom?.deliveryInstruction]);

  const updatePaymentState = useCallback(() => {
    const label = order?.paymentState ?? '';
    setPaymentState(label);
  }, [order?.paymentState]);

  useEffect(() => {
    if (order) {
      updateOrderNumber();
      updateOrderDate();
      updateDeliveryMode();
      updateDeliveryMethod();
      updateDeliveryMethodKey();
      updateDeliveryDate();
      updateShippingAddress();
      updateBillingAddress();
      updatePaymentInfo();
      updateDeliveryInstruction();
      updatePaymentState();
    }
  }, [
    order,
    updateDeliveryMethod,
    updateDeliveryMethodKey,
    updateOrderNumber,
    updateOrderDate,
    updateDeliveryMode,
    updateDeliveryDate,
    updatePaymentInfo,
    updateShippingAddress,
    updateBillingAddress,
    updateDeliveryInstruction,
    updatePaymentState,
  ]);

  return {
    orderNumber,
    orderDate,
    deliveryMode,
    deliveryMethod,
    deliveryMethodKey,
    deliveryDate,
    shippingAddress,
    billingAddress,
    billingName,
    paymentInfo,
    deliveryInstruction,
    paymentState,
  };
};

export default useOrderInfoData;
